/**
 * File-based playback tier for the UI sound system.
 *
 * Loads and caches the real audio recordings under src/assets/audio/ui/ and
 * tracks whether each file is a usable recording or the 0-byte placeholder.
 */

import pingFile from "../../assets/audio/ui/ping.wav";
import dingFile from "../../assets/audio/ui/ding.wav";
import dropFile from "../../assets/audio/ui/drop.wav";
import settleFile from "../../assets/audio/ui/settle.wav";
import clickFile from "../../assets/audio/ui/click.wav";
import whooshFile from "../../assets/audio/ui/whoosh.wav";
import upliftFile from "../../assets/audio/ui/uplift.wav";
import popFile from "../../assets/audio/ui/pop.wav";
import thudFile from "../../assets/audio/ui/thud.wav";
import warbleFile from "../../assets/audio/ui/warble.wav";
import buzzFile from "../../assets/audio/ui/buzz.wav";

import { UiSoundName } from "./uiSfx.types";

const FILE_URLS: Record<UiSoundName, string> = {
  ping: pingFile,
  ding: dingFile,
  drop: dropFile,
  settle: settleFile,
  click: clickFile,
  whoosh: whooshFile,
  uplift: upliftFile,
  pop: popFile,
  thud: thudFile,
  warble: warbleFile,
  buzz: buzzFile,
};

// File-status cache: 'unknown' until first probe, then 'usable' or 'empty'.
type FileStatus = "unknown" | "usable" | "empty";
export const fileStatus: Record<UiSoundName, FileStatus> = {
  ping: "unknown",
  ding: "unknown",
  drop: "unknown",
  settle: "unknown",
  click: "unknown",
  whoosh: "unknown",
  uplift: "unknown",
  pop: "unknown",
  thud: "unknown",
  warble: "unknown",
  buzz: "unknown",
};

// Cache one preloaded HTMLAudioElement per sound for snappy playback.
const audioCache: Partial<Record<UiSoundName, HTMLAudioElement>> = {};

export function getAudio(name: UiSoundName): HTMLAudioElement | undefined {
  if (typeof window === "undefined") return undefined;
  let a = audioCache[name];
  if (!a) {
    try {
      const url = FILE_URLS[name];
      if (!url) return undefined;
      a = new Audio(url);
      a.preload = "auto";
      a.addEventListener(
        "loadedmetadata",
        () => {
          fileStatus[name] =
            Number.isFinite(a!.duration) && a!.duration > 0.02
              ? "usable"
              : "empty";
        },
        { once: true }
      );
      a.addEventListener(
        "error",
        () => {
          fileStatus[name] = "empty";
        },
        { once: true }
      );
      audioCache[name] = a;
    } catch {
      return undefined;
    }
  }
  return a;
}
